<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useCurrency } from '@/composables/useCurrency.js'

const props = defineProps({
  data:        { type: Array,  required: true },
  periodLabel: { type: String, default: '' },
})

const { formatMoney } = useCurrency()

// ── Chart type ───────────────────────────────────────────────
const chartType = ref('bar') // 'bar' | 'line' | 'area'

// ── Series config ────────────────────────────────────────────
const SERIES = [
  { key: 'final_payment', label: 'Final Payments', color: 'var(--ok)'     },
  { key: 'extra_charge',  label: 'Extra Charges',  color: 'var(--warn)'   },
  { key: 'losses',        label: 'Losses',         color: 'var(--danger)' },
]

const REVENUE_KEYS = ['final_payment']
const SIDE_KEYS    = ['extra_charge', 'losses']

const activeKeys = ref(new Set(['final_payment', 'extra_charge', 'losses']))

function toggleKey(key) {
  const next = new Set(activeKeys.value)
  if (next.has(key) && next.size > 1) next.delete(key)
  else next.add(key)
  activeKeys.value = next
}

// ── SVG layout ───────────────────────────────────────────────
const W = 600, H = 220, PL = 52, PR = 12, PT = 12, PB = 32
const cW = W - PL - PR
const cH = H - PT - PB
const yB = PT + cH // y-coordinate of x-axis

// ── Helpers ──────────────────────────────────────────────────
const n = computed(() => props.data.length)

const activeRevKeys    = computed(() => REVENUE_KEYS.filter(k => activeKeys.value.has(k)))
const activeSideKeys   = computed(() => SIDE_KEYS.filter(k => activeKeys.value.has(k)))
const showExtraCharge  = computed(() => activeKeys.value.has('extra_charge'))
const showLosses       = computed(() => activeKeys.value.has('losses'))
const activeSeries     = computed(() => SERIES.filter(s => activeKeys.value.has(s.key)))

const seriesMap = Object.fromEntries(SERIES.map(s => [s.key, s]))

const visibleSeries = computed(() =>
  SERIES.filter(s => props.data.some(item => (item[s.key] || 0) > 0))
)

// ── Max value (for axis scaling) ─────────────────────────────
const maxValue = computed(() => {
  let max = 0
  props.data.forEach(item => {
    const revStack = activeRevKeys.value.reduce((s, k) => s + (item[k] || 0), 0)
    max = Math.max(max, revStack)
    if (showExtraCharge.value) max = Math.max(max, item.extra_charge || 0)
    if (showLosses.value)      max = Math.max(max, item.losses || 0)
  })
  return max || 1
})

// ── Coordinate functions ──────────────────────────────────────
// Bar: slot-centered
function xCenter(i) { return PL + (i + 0.5) * (cW / n.value) }
// Line/area: edge-to-edge
function xFor(i) {
  if (n.value <= 1) return PL + cW / 2
  return PL + (i / (n.value - 1)) * cW
}

function yFor(cents) {
  return PT + cH * (1 - Math.min(cents || 0, maxValue.value) / maxValue.value)
}

// ── Bar geometry ─────────────────────────────────────────────
const hasRev    = computed(() => activeRevKeys.value.length > 0)
const slotWidth = computed(() => cW / n.value)

// N-bar layout: revenue (stacked) + each active side series (deposit, losses)
// share a data slot. Bars are evenly sized with a 2px gap between them.
const barCount = computed(() => (hasRev.value ? 1 : 0) + activeSideKeys.value.length)
const GAP = 2

const barW = computed(() => {
  if (barCount.value === 0) return 0
  const total = Math.min(Math.max(slotWidth.value * 0.75, 8), 52)
  const gaps  = GAP * Math.max(barCount.value - 1, 0)
  return Math.max((total - gaps) / barCount.value, 3)
})

const slotBarsW = computed(() => barW.value * barCount.value + GAP * Math.max(barCount.value - 1, 0))

function barXAt(i, slotIdx) {
  return xCenter(i) - slotBarsW.value / 2 + slotIdx * (barW.value + GAP)
}

const revSlotIdx         = computed(() => hasRev.value ? 0 : -1)
const extraChargeSlotIdx = computed(() => showExtraCharge.value ? (hasRev.value ? 1 : 0) : -1)
const lossSlotIdx        = computed(() => {
  if (!showLosses.value) return -1
  return (hasRev.value ? 1 : 0) + (showExtraCharge.value ? 1 : 0)
})

// Stacked segments for a single bar
function stackedSegments(item) {
  const segs = []
  let cumY = yB
  for (const k of activeRevKeys.value) {
    const val = item[k] || 0
    if (val <= 0) continue
    const h = Math.max((val / maxValue.value) * cH, 1)
    segs.push({ key: k, color: seriesMap[k].color, y: cumY - h, h })
    cumY -= h
  }
  return segs
}

function sideBarData(item, key) {
  const val = item[key] || 0
  if (val <= 0) return null
  const h = Math.max((val / maxValue.value) * cH, 2)
  return { y: yB - h, h }
}

// ── Line / area paths ────────────────────────────────────────
function linePath(key) {
  if (!props.data.length) return ''
  return props.data
    .map((item, i) => `${i === 0 ? 'M' : 'L'}${xFor(i)},${yFor(item[key] || 0)}`)
    .join(' ')
}

function areaPath(key) {
  if (n.value === 0) return ''
  const inner = props.data.map((item, i) => `${xFor(i)},${yFor(item[key] || 0)}`).join(' L')
  return `M${xFor(0)},${yB} L${inner} L${xFor(n.value - 1)},${yB} Z`
}

// ── Y-axis ticks ─────────────────────────────────────────────
const yTicks = computed(() =>
  Array.from({ length: 5 }, (_, i) => ({
    cents: Math.round((maxValue.value * i) / 4),
    y:     PT + cH * (1 - i / 4),
  }))
)

function shortMoney(cents) {
  if (cents === 0) return '0'
  // Format using currency, then abbreviate large numbers
  const formatted = formatMoney(cents)
  // Show abbreviated if >= 1000 (100000 cents)
  if (cents >= 1000000) {
    return formatMoney(Math.round(cents / 10000) * 10000)
      .replace(/,\d{3}$/, 'k').replace(/\.\d+$/, '')
  }
  return formatted.replace(/\.00$/, '').replace(/,00$/, '')
}

// ── Hover state ──────────────────────────────────────────────
const hoveredIdx = ref(null)
const tooltipItem = computed(() =>
  hoveredIdx.value !== null ? props.data[hoveredIdx.value] : null
)

function xHover(i) {
  return chartType.value === 'bar' ? xCenter(i) : xFor(i)
}

// Tooltip left % with edge clamping
function tooltipLeft(i) {
  const pct = ((xHover(i) - PL) / cW) * 100
  return Math.min(Math.max(pct, 15), 85) + '%'
}

// ── Fullscreen ───────────────────────────────────────────────
const isFullscreen = ref(false)

function toggleFullscreen() { isFullscreen.value = !isFullscreen.value }
function onKeydown(e) { if (e.key === 'Escape') isFullscreen.value = false }
onMounted(()  => window.addEventListener('keydown', onKeydown))
onUnmounted(() => window.removeEventListener('keydown', onKeydown))

// Chart type icons (24×24 SVG paths)
const TYPE_ICONS = [
  { v: 'bar',  title: 'Bar chart',
    d: 'M3 3v18h18M7 16v-5m4 5V8m4 8v-2' },
  { v: 'line', title: 'Line chart',
    d: 'M3 17l4-6 4 2 4-5 4 3' },
  { v: 'area', title: 'Area chart',
    d: 'M3 17l4-6 4 2 4-5 4 3v4H3z' },
]
</script>

<template>
  <!-- Card wrapper — normal or fullscreen -->
  <div :class="isFullscreen
         ? 'fixed inset-0 z-50 bg-white flex flex-col p-8 overflow-auto'
         : 'relative'">

    <!-- ── Header row ──────────────────────────────────────── -->
    <div class="flex items-start justify-between gap-3 mb-4 flex-wrap">
      <h2 class="text-sm font-semibold shrink-0"
          style="font-family: var(--display); color: var(--ink); letter-spacing: -0.01em;">
        Revenue — {{ periodLabel }}
      </h2>

      <div class="flex items-center gap-2 flex-wrap">

        <!-- Series toggles -->
        <div class="flex items-center gap-1.5 flex-wrap">
          <button v-for="s in visibleSeries" :key="s.key"
                  @click="toggleKey(s.key)"
                  class="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition-all border"
                  :style="activeKeys.has(s.key)
                    ? `background: ${s.color}15; border-color: ${s.color}40; color: ${s.color};`
                    : 'background: transparent; border-color: var(--line); color: var(--muted);'">
            <span class="w-2 h-2 rounded-full shrink-0"
                  :style="activeKeys.has(s.key) ? `background: ${s.color};` : 'background: var(--lineStrong);'"></span>
            {{ s.label }}
          </button>
        </div>

        <!-- Chart type switcher -->
        <div class="flex items-center rounded-lg overflow-hidden border shrink-0" style="border-color: var(--line);">
          <button v-for="t in TYPE_ICONS" :key="t.v"
                  @click="chartType = t.v"
                  :title="t.title"
                  class="w-7 h-7 flex items-center justify-center transition-colors"
                  :style="chartType === t.v
                    ? 'background: var(--ink); color: var(--surface);'
                    : 'background: var(--surface); color: var(--muted);'">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path :d="t.d" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <!-- Fullscreen button -->
        <button @click="toggleFullscreen"
                :title="isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand'"
                class="w-7 h-7 flex items-center justify-center rounded-lg border transition-colors shrink-0"
                style="border-color: var(--line); color: var(--muted);">
          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path v-if="!isFullscreen"
                  d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
                  stroke-linecap="round" stroke-linejoin="round"/>
            <path v-else
                  d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- ── Chart area ──────────────────────────────────────── -->
    <div class="relative" :class="isFullscreen ? 'flex-1 min-h-0' : ''"
         @mouseleave="hoveredIdx = null">

      <svg :viewBox="`0 0 ${W} ${H}`"
           class="w-full select-none"
           :style="isFullscreen ? 'height: 100%;' : 'height: 240px;'">

        <!-- Y-axis gridlines + labels -->
        <g v-for="tick in yTicks" :key="tick.y">
          <line :x1="PL" :y1="tick.y" :x2="W - PR" :y2="tick.y"
                stroke="var(--line)" stroke-width="1"/>
          <text :x="PL - 6" :y="tick.y + 3.5"
                text-anchor="end" font-size="8.5" fill="var(--muted)"
                style="font-family: var(--mono);">
            {{ shortMoney(tick.cents) }}
          </text>
        </g>

        <!-- X-axis baseline -->
        <line :x1="PL" :y1="yB" :x2="W - PR" :y2="yB"
              stroke="var(--lineStrong)" stroke-width="1"/>

        <!-- ════════════ BAR CHART ════════════ -->
        <g v-if="chartType === 'bar'">
          <g v-for="(item, i) in data" :key="'bar' + i">

            <!-- Revenue stacked bar -->
            <g v-if="hasRev && stackedSegments(item).length">
              <rect v-for="seg in stackedSegments(item)" :key="seg.key"
                    :x="barXAt(i, revSlotIdx)"
                    :y="seg.y"
                    :width="barW"
                    :height="seg.h"
                    :fill="seg.color"
                    :rx="hoveredIdx === i ? 3 : 2"
                    :opacity="hoveredIdx === i ? 1 : 0.82"
                    style="transition: opacity 0.1s;"/>
            </g>

            <!-- Extra Charges bar -->
            <rect v-if="showExtraCharge && sideBarData(item, 'extra_charge')"
                  :x="barXAt(i, extraChargeSlotIdx)"
                  :y="sideBarData(item, 'extra_charge').y"
                  :width="barW"
                  :height="sideBarData(item, 'extra_charge').h"
                  fill="var(--warn)"
                  :rx="hoveredIdx === i ? 3 : 2"
                  :opacity="hoveredIdx === i ? 1 : 0.75"
                  style="transition: opacity 0.1s;"/>

            <!-- Losses bar -->
            <rect v-if="showLosses && sideBarData(item, 'losses')"
                  :x="barXAt(i, lossSlotIdx)"
                  :y="sideBarData(item, 'losses').y"
                  :width="barW"
                  :height="sideBarData(item, 'losses').h"
                  fill="var(--danger)"
                  :rx="hoveredIdx === i ? 3 : 2"
                  :opacity="hoveredIdx === i ? 1 : 0.75"
                  style="transition: opacity 0.1s;"/>

            <!-- Empty bar placeholder -->
            <rect v-if="!stackedSegments(item).length && !sideBarData(item, 'extra_charge') && !sideBarData(item, 'losses')"
                  :x="xCenter(i) - barW / 2"
                  :y="yB - 2"
                  :width="barW"
                  height="2"
                  fill="var(--line)" rx="1"/>

            <!-- Invisible hover area -->
            <rect :x="xCenter(i) - slotWidth / 2"
                  :y="PT"
                  :width="slotWidth"
                  :height="cH"
                  fill="transparent"
                  @mouseenter="hoveredIdx = i"/>
          </g>
        </g>

        <!-- ════════════ LINE CHART ════════════ -->
        <g v-else-if="chartType === 'line'">
          <g v-for="s in activeSeries" :key="'line' + s.key">
            <path :d="linePath(s.key)"
                  fill="none"
                  :stroke="s.color"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"/>
            <circle v-for="(item, i) in data" :key="'dot' + i"
                    :cx="xFor(i)" :cy="yFor(item[s.key] || 0)"
                    :r="hoveredIdx === i ? 4 : 2.5"
                    :fill="s.color"
                    style="transition: r 0.1s;"/>
          </g>
          <!-- Hover slots -->
          <rect v-for="(_, i) in data" :key="'hs' + i"
                :x="xFor(i) - cW / Math.max(n - 1, 1) / 2"
                :y="PT" :width="cW / Math.max(n - 1, 1)" :height="cH"
                fill="transparent" @mouseenter="hoveredIdx = i"/>
        </g>

        <!-- ════════════ AREA CHART ════════════ -->
        <g v-else-if="chartType === 'area'">
          <g v-for="s in activeSeries" :key="'area' + s.key">
            <path :d="areaPath(s.key)" :fill="s.color" fill-opacity="0.12"/>
            <path :d="linePath(s.key)"
                  fill="none" :stroke="s.color"
                  stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"/>
            <circle v-for="(item, i) in data" :key="'adot' + i"
                    :cx="xFor(i)" :cy="yFor(item[s.key] || 0)"
                    :r="hoveredIdx === i ? 4 : 0"
                    :fill="s.color"
                    style="transition: r 0.1s;"/>
          </g>
          <!-- Hover slots -->
          <rect v-for="(_, i) in data" :key="'ahs' + i"
                :x="xFor(i) - cW / Math.max(n - 1, 1) / 2"
                :y="PT" :width="cW / Math.max(n - 1, 1)" :height="cH"
                fill="transparent" @mouseenter="hoveredIdx = i"/>
        </g>

        <!-- Hover vertical guideline -->
        <line v-if="hoveredIdx !== null"
              :x1="xHover(hoveredIdx)" :y1="PT"
              :x2="xHover(hoveredIdx)" :y2="yB"
              stroke="var(--ink)" stroke-width="1"
              stroke-dasharray="3 3" opacity="0.2"/>

        <!-- X-axis labels -->
        <text v-for="(item, i) in data" :key="'xl' + i"
              :x="chartType === 'bar' ? xCenter(i) : xFor(i)"
              :y="H - 6"
              text-anchor="middle"
              font-size="9"
              :fill="hoveredIdx === i ? 'var(--ink)' : 'var(--muted)'"
              :font-weight="hoveredIdx === i ? '600' : '400'"
              style="font-family: var(--mono);">
          {{ item.short }}
        </text>
      </svg>

      <!-- ── Tooltip ─────────────────────────────────────── -->
      <Transition name="tip">
        <div v-if="tooltipItem"
             class="absolute top-2 pointer-events-none z-10"
             :style="`left: ${tooltipLeft(hoveredIdx)}; transform: translateX(-50%);`">
          <div class="rounded-xl shadow-xl px-3.5 py-3 text-xs"
               style="min-width: 160px; background: var(--ink); color: var(--surface);">
            <p class="font-semibold mb-2.5 text-xs"
               style="font-family: var(--display); color: var(--surface); letter-spacing: -0.01em;">
              {{ tooltipItem.label }}
            </p>
            <div class="space-y-1.5">
              <div v-for="s in activeSeries.filter(s => (tooltipItem[s.key] || 0) > 0)" :key="'tip' + s.key"
                   class="flex items-center justify-between gap-5">
                <div class="flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full shrink-0"
                        :style="`background: ${s.color};`"></span>
                  <span style="color: var(--muted);">{{ s.label }}</span>
                </div>
                <span style="font-family: var(--mono); font-weight: 600;">
                  {{ formatMoney(tooltipItem[s.key] || 0) }}
                </span>
              </div>
              <!-- Always show total revenue at bottom -->
              <div class="flex items-center justify-between gap-5 pt-1.5 mt-0.5" style="border-top: 1px solid color-mix(in oklab, var(--surface) 15%, transparent);">
                <div class="flex items-center gap-1.5">
                  <span class="w-2 h-2 rounded-full shrink-0" style="background: var(--warn);"></span>
                  <span style="color: var(--muted);">Net revenue</span>
                </div>
                <span style="font-family: var(--mono); font-weight: 700; color: var(--warn);">
                  {{ formatMoney(tooltipItem.total || 0) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </div>

    <!-- Fullscreen ESC hint -->
    <p v-if="isFullscreen" class="text-center mt-4 text-xs" style="color: var(--muted);">
      Press <kbd class="px-1.5 py-0.5 rounded font-mono" style="background: var(--surfaceAlt); color: var(--muted);">Esc</kbd> to exit
    </p>
  </div>
</template>

<style scoped>
.tip-enter-active, .tip-leave-active { transition: opacity 0.12s ease; }
.tip-enter-from, .tip-leave-to       { opacity: 0; }
</style>
